import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../components/WebsiteLayout';
import Reveal from '../components/Reveal';
import { useLanguage } from '../../../hooks/useLanguage';

// Videos are served from the public S3 bucket, same as MissionOS.tsx and
// Applications.tsx — not from /assets/website/, which holds no video files.
const S3 = 'https://thoughtfuloasis-public.s3.us-east-1.amazonaws.com';
const HERO_VIDEO = `${S3}/vmo-hero.mp4`;
const CASE_DETAIL_VIDEO = `${S3}/vmo-detail.mp4`;
const FEATURE_SEARCH_TERMS_VIDEO = `${S3}/vmo-zoektermen.mp4`;
const FEATURE_PAGE_SEARCH_VIDEO = `${S3}/vmo-pagina-zoeken.mp4`;
const FEATURE_DOCUMENT_VIDEO = `${S3}/vmo-documentonderzoek.mp4`;

const VraagMijnOverheidProductPage: React.FC = () => {
  // Default to Dutch on /vmo (per-route override; global default stays 'en').
  const [language] = useLanguage('nl');
  const en = language === 'en';

  // Five videos on one page, so none of them autoplays on load: each starts
  // when it scrolls into view and pauses when it leaves. Kept local to this
  // page rather than in the shared website runtime.
  const mainRef = React.useRef<HTMLElement>(null);
  React.useEffect(() => {
    const root = mainRef.current;
    if (!root) return;
    const videos = Array.from(root.querySelectorAll<HTMLVideoElement>('video'));
    if (!videos.length) return;
    const play = (v: HTMLVideoElement) => {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    if (!('IntersectionObserver' in window)) {
      videos.forEach(play);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const v = e.target as HTMLVideoElement;
          if (e.isIntersecting) play(v);
          else v.pause();
        });
      },
      { threshold: 0.25 },
    );
    videos.forEach((v) => io.observe(v));
    return () => io.disconnect();
  }, []);

  const customCta = (
    <div className="site-end__cta">
      <span className="site-end__label">
        {en ? 'Plan a conversation' : 'Plan een gesprek'}
      </span>
      <h2 className="site-end__title">
        {en
          ? 'How many citizens and civil servants are we going to help?'
          : 'Hoeveel burgers en ambtenaren gaan we helpen?'}
      </h2>
      <p className="site-end__sub">
        {en
          ? "We'll work through with you what can be prevented at the front, and what that saves per request."
          : 'We rekenen met je mee wat aan de voorkant te voorkomen valt en wat dat per verzoek scheelt.'}
      </p>
      <div className="site-end__action">
        <Link className="btn btn-primary" to="/contact?prefill=vmo-intake">
          {en ? 'Schedule an intake' : 'Plan een intake'}
        </Link>
      </div>
    </div>
  );

  const customPartners = (
    <div className="site-end__partners">
      <p className="site-end__partners-label">
        {en ? 'In collaboration with' : 'In samenwerking met'}
      </p>
      <div className="site-end__partners-logos">
        <a
          href="https://www.rijksoverheid.nl/ministeries/ministerie-van-justitie-en-veiligheid"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="/assets/website/logos/jenv-blauw.svg"
            alt={en ? 'Ministry of Justice and Security' : 'Ministerie van Justitie en Veiligheid'}
          />
        </a>
      </div>
    </div>
  );

  const descriptionMeta = en
    ? 'Front portal for Dutch FOI and information requests. Live in pilot with the Ministry of Justice and Security. Prevents needless requests, speeds up the rest.'
    : 'Voorportaal voor Woo- en informatieverzoeken, live in pilot bij Justitie en Veiligheid. Voorkomt onnodige verzoeken en versnelt de verwerking van de rest.';

  return (
    <WebsiteLayout
      bodyClass="vmo"
      footerVariant="vmo"
      customCta={customCta}
      customPartners={customPartners}
    >
      <Helmet>
        <title>VraagMijnOverheid · Thoughtful Oasis</title>
        <meta name="description" content={descriptionMeta} />
        <link rel="canonical" href="https://thoughtful-oasis.com/vmo" />
        <meta property="og:type" content="product" />
        <meta property="og:title" content="VraagMijnOverheid · Thoughtful Oasis" />
        <meta property="og:description" content={descriptionMeta} />
        <meta property="og:url" content="https://thoughtful-oasis.com/vmo" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <meta property="og:locale" content={en ? 'en_GB' : 'nl_NL'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="VraagMijnOverheid · Thoughtful Oasis" />
        <meta name="twitter:description" content={descriptionMeta} />
        <meta name="twitter:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "VraagMijnOverheid",
          "applicationCategory": "GovernmentApplication",
          "operatingSystem": "Web",
          "description": ${JSON.stringify(descriptionMeta)},
          "url": "https://thoughtful-oasis.com/vmo",
          "provider": {
            "@type": "Organization",
            "name": "Thoughtful Oasis",
            "url": "https://thoughtful-oasis.com/"
          }
        }`}</script>
      </Helmet>

      <main className="vmo" ref={mainRef}>
        {/* Series ribbon: VraagMijnOverheid is powered by oasis_0 · Themis. */}
        <Link
          to="/models#series"
          className="ribbon"
          aria-label={en ? 'oasis_0 model series' : 'oasis_0-modelserie'}
        >
          <span className="ribbon__inner">
            <span className="ribbon__dot" aria-hidden="true"></span>
            <span>Powered by oasis_0 · Themis</span>
          </span>
        </Link>
        {/* §1 Hero */}
        <header className="vmo-hero">
          <div className="container">
            <div className="vmo-hero__intro">
              <a
                className="label label--live"
                href="https://www.rijksoverheid.nl/ministeries/ministerie-van-justitie-en-veiligheid/contact/woo-verzoek-indienen"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="live-dot"></span>
                {en
                  ? 'Live in pilot with the Ministry of Justice and Security'
                  : 'Live in pilot bij het ministerie van Justitie en Veiligheid'}
              </a>
              <h1>
                {en ? (
                  <>
                    VraagMijnOverheid: openness{' '}
                    <span className="accent">during the request</span>.
                  </>
                ) : (
                  <>
                    VraagMijnOverheid: openbaarheid{' '}
                    <span className="accent">tijdens het aanvragen</span>.
                  </>
                )}
              </h1>
              <p className="vmo-hero__sub">
                {en
                  ? 'VraagMijnOverheid is a public front portal for Woo (Dutch FOI) and information requests. It helps users draft their request by unpacking it into sub-questions that are answered straight away with available, already published documents. That prevents unnecessary requests, and speeds up the handling of the ones that are filed.'
                  : 'VraagMijnOverheid is een publiek voorportaal voor Woo- en informatieverzoeken. Het helpt gebruikers bij het opstellen van het verzoek, door het uit te pakken in deelvragen die direct beantwoord worden met beschikbare en reeds gepubliceerde documenten. Zo voorkomen we onnodige verzoeken, en versnellen we de verwerking van ingediende verzoeken.'}
              </p>
              <div className="vmo-hero__cta">
                <Link className="btn btn-primary" to="/contact?prefill=vmo-intake">
                  {en ? 'Schedule a conversation' : 'Plan een gesprek'}
                </Link>
                <a
                  className="btn btn-secondary"
                  href="https://woo.thoughtful-oasis.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {en ? 'See the tool live' : 'Bekijk de tool live'}
                </a>
              </div>
            </div>
            <div className="vmo-hero__media vmo-hero__media--video">
              <div className="vmo-hero__overlay">
                <span className="vmo-hero__badge">{en ? 'Demo video' : 'Demo video'}</span>
              </div>
              <video
                muted
                loop
                playsInline
                preload="metadata"
                poster="/assets/website/photos/vmo-screenshot.png"
                src={HERO_VIDEO}
                aria-label={
                  en
                    ? 'VraagMijnOverheid — a citizen asks their question in plain language'
                    : 'VraagMijnOverheid — burger stelt zijn vraag in eigen taal'
                }
              />
            </div>
            <p className="vmo-hero__try">
              <a
                className="btn btn-primary"
                href="https://woo.thoughtful-oasis.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                {en ? 'Try it out' : 'Probeer het uit'}
              </a>
            </p>
          </div>
        </header>

        {/* §2 The problem in numbers */}
        <section className="section vmo-dark">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">
                {en ? 'The problem in numbers' : 'Het probleem in cijfers'}
              </span>
              <h2>
                {en
                  ? 'The Woo works in practice for neither citizens nor civil servants.'
                  : 'De uitvoering van de Woo werkt niet voor burgers, én niet voor ambtenaren.'}
              </h2>
              <p className="lede" style={{ marginTop: 24 }}>
                {en
                  ? 'One in three Woo requests misses the deadline, and rarely because of the law. The time goes into internal approval and stakeholder review, and that runs on when you do not know what the requester is actually after. Part of those requests could have been an information request instead: faster for both sides, but too little used. So the gains sit before the request, not after it.'
                  : 'Een op de drie Woo-verzoeken haalt de termijn niet, en dat komt zelden door de wet. De tijd gaat op aan interne goedkeuring en zienswijze, en dat loopt op als je niet weet wat de verzoeker precies zoekt. Een deel van die verzoeken had bovendien een informatieverzoek kunnen zijn: sneller voor beide kanten, maar te weinig gebruikt. De winst zit dus vóór het verzoek, niet erna.'}
              </p>
            </Reveal>
          </div>

          <Reveal className="vmo-stats-block">
            <p className="vmo-stats-block__title">
              {en ? 'National picture · reference year 2024' : 'Landelijk beeld · peiljaar 2024'}
            </p>
            <div className="vmo-stats">
              <div className="vmo-stat">
                <p className="vmo-stat__num vmo-stat__num--accent">€150 mln</p>
                <p className="vmo-stat__label">
                  {en ? 'Total annual execution cost' : 'Totale uitvoeringskosten per jaar'}
                </p>
                <p className="vmo-stat__note">
                  {en
                    ? 'For handling Woo requests at the government organisations included in the study.'
                    : 'Voor de afhandeling van Woo-verzoeken bij overheidsorganisaties in scope van het onderzoek.'}
                </p>
              </div>
              <div className="vmo-stat">
                <p className="vmo-stat__num">955 fte</p>
                <p className="vmo-stat__label">
                  {en ? 'Full-time jobs per year' : 'Voltijdbanen per jaar'}
                </p>
                <p className="vmo-stat__note">
                  {en
                    ? 'About 1.3 million civil-servant hours spent on Woo handling each year.'
                    : 'Circa 1,3 miljoen uur ambtenaarschap, jaarlijks ingezet op Woo-afhandeling.'}
                </p>
              </div>
              <div className="vmo-stat">
                <p className="vmo-stat__num">25.000</p>
                <p className="vmo-stat__label">
                  {en ? 'Woo requests in 2024' : 'Woo-verzoeken in 2024'}
                </p>
                <p className="vmo-stat__note">
                  {en
                    ? 'Received by 250+ participating organisations: ministries, municipalities, provinces, water authorities, executive agencies.'
                    : 'Ontvangen door 250+ deelnemende organisaties: ministeries, gemeenten, provincies, waterschappen, uitvoeringsorganisaties.'}
                </p>
              </div>
            </div>
            <p className="vmo-stats-block__source">
              {en ? (
                <>
                  Source: BZK, Woo execution-burden study, May 2026 (annex to the{' '}
                  <a
                    href="https://app.1848.nl/document/tkapi/585141"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    open-government progress letter of 13-05-2026
                  </a>{' '}
                  to the House of Representatives).
                </>
              ) : (
                <>
                  Bron: BZK, onderzoek uitvoeringslasten Woo, mei 2026 (bijlage bij{' '}
                  <a
                    href="https://app.1848.nl/document/tkapi/585141"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    voortgangsbrief open overheid van 13-05-2026
                  </a>{' '}
                  aan de Tweede Kamer).
                </>
              )}
            </p>
          </Reveal>

          <Reveal className="vmo-stats-block">
            <p className="vmo-stats-block__title">
              {en
                ? 'What that means for your organisation'
                : 'Wat dat betekent voor jouw organisatie'}
            </p>
            <div className="vmo-stats">
              <div className="vmo-stat">
                <p className="vmo-stat__num">{en ? '102 hours' : '102 uur'}</p>
                <p className="vmo-stat__label">
                  {en ? 'Average per request' : 'Gemiddeld per verzoek'}
                </p>
                <p className="vmo-stat__note">
                  {en
                    ? 'At ministries. About 13 person-days per Woo request, from intake to closing.'
                    : 'Bij ministeries. Circa 13 mensdagen per Woo-verzoek, van binnenkomst tot afhandeling.'}
                </p>
              </div>
              <div className="vmo-stat">
                <p className="vmo-stat__num vmo-stat__num--accent">90%</p>
                <p className="vmo-stat__label">
                  {en
                    ? 'Does not use good search software'
                    : 'Gebruikt geen goede zoeksoftware'}
                </p>
                <p className="vmo-stat__note">
                  {en
                    ? 'Of the 147 organisations that answered the question, 90% use good search software rarely or not at all. Among those that do, 64% see shorter lead times.'
                    : 'Van de 147 organisaties die de vraag beantwoordden gebruikt 90% niet of onregelmatig goede zoeksoftware. Van wie het wél doet, ziet 64% kortere doorlooptijden.'}
                </p>
              </div>
              <div className="vmo-stat">
                <p className="vmo-stat__num">30%</p>
                <p className="vmo-stat__label">
                  {en
                    ? 'Time spent on the 10% most complex requests'
                    : 'Tijd voor de 10% meest complexe verzoeken'}
                </p>
                <p className="vmo-stat__note">
                  {en
                    ? 'A minority of complex cases takes a disproportionate share of execution capacity.'
                    : 'Een minderheid van complexe zaken legt een onevenredig beslag op de uitvoering.'}
                </p>
              </div>
            </div>
            <p className="vmo-stats-block__source">
              {en
                ? 'Source: BZK, Woo execution-burden study, May 2026.'
                : 'Bron: BZK, onderzoek uitvoeringslasten Woo, mei 2026.'}
            </p>
          </Reveal>
        </section>

        {/* §2b Three recommended practices */}
        <section className="section">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">
                {en ? 'Researched and recommended' : 'Onderzocht en aanbevolen'}
              </span>
              <h2>
                {en
                  ? 'The BZK study names three practices that reduce the execution burden. VraagMijnOverheid successfully applies two of them.'
                  : 'Het BZK-onderzoek noemt drie werkwijzen die de uitvoeringslast verlagen. VraagMijnOverheid past er twee succesvol toe.'}
              </h2>
              <p className="lede" style={{ marginTop: 24 }}>
                {en
                  ? '"Timely and clear contact with the requester" plays an important role in limiting the execution burden, the study states. Of the twelve practices examined, these are the three the report recommends.'
                  : '"Tijdig en helder contact met de verzoeker" speelt een belangrijke rol bij het beperken van de uitvoeringslasten, staat in het onderzoek. Van de twaalf onderzochte werkwijzen zijn dit de drie die het rapport aanbeveelt.'}
              </p>
            </Reveal>
          </div>
          <Reveal className="vmo-measures">
            <div className="vmo-measure vmo-measure--primary">
              <p className="vmo-measure__num">{en ? '01 · our core' : '01 · onze kern'}</p>
              <h3>
                {en
                  ? 'Engage the requester early.'
                  : 'Verzoeker vroegtijdig betrekken.'}
              </h3>
              <p>
                {en
                  ? "This is exactly what VraagMijnOverheid does. The chatbot unpacks the citizen's first question into sub-questions, in a single clear window. Every step after that lies open: which search terms were used, which documents that returned, and where those were published. The citizen steers from there. If they do file, the civil servant gets that same overview as a report, and sees straight away what the requester is looking for and with what intent."
                  : 'Dit is precies wat VraagMijnOverheid doet. De chatbot pakt de eerste vraag van de burger uit in deelvragen, in één overzichtelijk venster. Elke stap daarna ligt open: welke zoektermen zijn gebruikt, welke documenten dat opleverde, en waar die gepubliceerd staan. De burger stuurt daarop bij. Dient hij toch in, dan krijgt de ambtenaar datzelfde overzicht als rapport, en ziet meteen wat de verzoeker zoekt en met welke intentie.'}
              </p>
              <span className="vmo-measure__status vmo-measure__status--in">
                {en ? '✓ The core of VraagMijnOverheid' : '✓ De kern van VraagMijnOverheid'}
              </span>
            </div>
            <div className="vmo-measure vmo-measure--secondary">
              <p className="vmo-measure__num">02</p>
              <h3>{en ? 'Good search software.' : 'Goede zoeksoftware.'}</h3>
              <p>
                {en
                  ? 'Searching in your own words, in documents written in government jargon. On the publication platforms that often fails: even civil servants regularly cannot find their own Woo decisions there.'
                  : 'Zoeken in eigen taal, in documenten die in overheidsjargon zijn geschreven. Op de publicatieplatforms lukt dat vaak niet: zelfs ambtenaren vinden hun eigen Woo-besluiten er geregeld niet terug.'}
              </p>
              <Link className="vmo-measure__status vmo-measure__status--in" to="/platform">
                {en ? '✓ The core of our company' : '✓ De kern van ons bedrijf'}
              </Link>
            </div>
            <div className="vmo-measure vmo-measure--tertiary">
              <p className="vmo-measure__num">03</p>
              <h3>
                {en
                  ? 'Shortening the stakeholder-review period.'
                  : 'Verkorten zienswijzetermijn.'}
              </h3>
              <p>
                {en
                  ? 'A process change further along the Woo track, after the formal request has been received.'
                  : 'Procesinrichting verderop in het Woo-traject, na binnenkomst van het formele verzoek.'}
              </p>
              <span className="vmo-measure__status vmo-measure__status--out">
                {en ? 'Out of scope' : 'Buiten scope'}
              </span>
            </div>
          </Reveal>
          <Reveal as="p" className="vmo-measures-source">
            {en ? (
              <>
                Source: BZK, Woo execution-burden study, May 2026 (annex to the{' '}
                <a
                  href="https://app.1848.nl/document/tkapi/585141"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  open-government progress letter of 13-05-2026
                </a>
                ).
              </>
            ) : (
              <>
                Bron: BZK, onderzoek uitvoeringslasten Woo, mei 2026 (bijlage bij{' '}
                <a
                  href="https://app.1848.nl/document/tkapi/585141"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  voortgangsbrief open overheid van 13-05-2026
                </a>
                ).
              </>
            )}
          </Reveal>
        </section>

        {/* §3 How it works */}
        <section className="section">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">{en ? 'How it works' : 'Hoe het werkt'}</span>
              <h2>
                {en
                  ? 'One portal, from first question to refined request.'
                  : 'Eén portaal van eerste vraag naar gepreciseerd verzoek.'}
              </h2>
              <p className="lede" style={{ marginTop: 24 }}>
                {en
                  ? 'VraagMijnOverheid is an application that requires no integration with internal infrastructure. Show us where you publish and where citizens can file requests today, and we can be live within days.'
                  : 'VraagMijnOverheid is een applicatie die geen integratie vereist met interne infrastructuur. Toon ons waar jullie publiceren en waar burgers nu verzoeken kunnen indienen, en binnen een aantal dagen kunnen we live zijn.'}
              </p>
            </Reveal>
          </div>
          <Reveal className="vmo-steps">
            <div className="vmo-step">
              <p className="vmo-step__num">{en ? '01 · Citizen' : '01 · Burger'}</p>
              <h3>
                {en ? 'Ask in plain language.' : 'Vraag stellen in eigen taal.'}
              </h3>
              <p className="vmo-step__body">
                {en
                  ? 'The citizen asks their question in a chat, in everyday language. The answer comes back in plain language, guiding them step by step through the process and through what is already available. In line with art. 4.7 Woo: low-threshold, service-minded, de-legalising.'
                  : 'De burger stelt zijn vraag in een chat, in gewone taal. Het antwoord komt in eenvoudig Nederlands, met stap voor stap de weg in het proces en in wat er al ligt. In lijn met art. 4.7 Woo: laagdrempelig, klantvriendelijk, dejuridiserend.'}
              </p>
              <div className="vmo-step__example">
                {en
                  ? '"How well prepared are the police for crisis situations?"'
                  : '"Hoe goed is de politie voorbereid op crisissituaties?"'}
              </div>
            </div>
            <div className="vmo-step">
              <p className="vmo-step__num">{en ? '02 · System' : '02 · Systeem'}</p>
              <h3>
                {en
                  ? 'Clarify, and show public documents already available.'
                  : 'Preciseren en al publieke documenten tonen.'}
              </h3>
              <p className="vmo-step__body">
                {en
                  ? 'VraagMijnOverheid keeps asking until the question is sharp: which period, which kind of records, which organisation, and what the citizen needs it for. Every summary goes back to the citizen first: is this right?'
                  : 'VraagMijnOverheid vraagt terug tot de vraag scherp is: welke periode, welk soort stukken, welke organisatie, en waarvoor de burger het nodig heeft. Elke samenvatting gaat eerst terug naar de burger: klopt dit?'}
              </p>
              <div className="vmo-step__example">
                {en
                  ? '"What kind of crisis situations do you mean? And do you want information from the police or from the ministry? And from which year onward?"'
                  : '"Wat voor crisissituaties bedoel je? En wil je informatie van de politie of van het ministerie? En vanaf welk jaar?"'}
              </div>
            </div>
            <div className="vmo-step">
              <p className="vmo-step__num">{en ? '03 · Citizen' : '03 · Burger'}</p>
              <h3>
                {en
                  ? 'Choose: satisfied, information request, or formal Woo.'
                  : 'Kiezen: tevreden, informatieverzoek of formeel Woo.'}
              </h3>
              <p className="vmo-step__body">
                {en
                  ? 'VraagMijnOverheid takes care to explain the process and the choices to users. That helps them choose between an information request and a Woo request.'
                  : 'VraagMijnOverheid draagt zorg om het proces en de keuzes toe te lichten aan de gebruikers. Zo kan het helpen om te kiezen voor een informatieverzoek, of een Woo-verzoek.'}
              </p>
              <div className="vmo-step__example vmo-step__example--choice">
                <div className="vmo-step__example-choice vmo-step__example-choice--accent">
                  <span>
                    {en ? 'Quick information request' : 'Snel informatieverzoek'}
                  </span>
                  <span className="vmo-step__example-choice-arrow">→</span>
                </div>
                <div className="vmo-step__example-choice">
                  <span>{en ? 'Formal Woo request' : 'Formeel Woo-verzoek'}</span>
                  <span className="vmo-step__example-choice-arrow">→</span>
                </div>
                <div className="vmo-step__example-choice">
                  <span>{en ? 'Download as PDF' : 'Download als PDF'}</span>
                  <span className="vmo-step__example-choice-arrow">↓</span>
                </div>
              </div>
            </div>
            <div className="vmo-step">
              <p className="vmo-step__num">{en ? '04 · Officer' : '04 · Ambtenaar'}</p>
              <h3>
                {en
                  ? 'As an attachment to a regular Woo request.'
                  : 'Als bijlage bij een regulier Woo-verzoek.'}
              </h3>
              <p className="vmo-step__body">
                {en
                  ? 'The search report arrives as a PDF in your existing Woo mailbox, including all the preparatory work. That makes VraagMijnOverheid a quickly deployable addition to a Woo process that keeps changing.'
                  : 'Het zoekrapport komt als PDF binnen via de bestaande Woo-mailbox, inclusief het volledige voorwerk. Zo is VraagMijnOverheid een snel toepasbare aanvulling op een continu veranderend Woo-proces.'}
              </p>
              <div className="vmo-step__example vmo-step__example--file vmo-step__example--file-tall">
                <div className="vmo-step__example-icon">PDF</div>
                <div className="vmo-step__example-file">
                  <span className="vmo-step__example-file-name">
                    {en ? 'report_crisis_situations.pdf' : 'rapport_crisissituaties.pdf'}
                  </span>
                  <span className="vmo-step__example-file-meta">
                    {en
                      ? '4 sub-questions · 2 answered · 24 documents found · 9 relevant · 12 search terms · 1 conversation summary'
                      : '4 subvragen · 2 beantwoord · 24 gevonden documenten · 9 relevant · 12 zoektermen · 1 gespreksoverzicht'}
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
        </section>

        {/* §4 Pilot JenV */}
        <section className="section">
          <div className="container">
            <Reveal className="section-head">
              <span className="label label--live">
                <span className="live-dot"></span>
                {en ? 'Live · 15 July – 15 September' : 'Live · 15 juli – 15 september'}
              </span>
              <h2>
                {en
                  ? 'We are testing this live with the Ministry of Justice and Security.'
                  : 'We testen dit live met het ministerie van Justitie en Veiligheid.'}
              </h2>
            </Reveal>

            <Reveal as="article" className="case-feature">
              <div className="case-feature__image case-feature__image--vmo case-feature__image--has-media">
                <div className="case-feature__media case-feature__media--left">
                  <video
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    src={CASE_DETAIL_VIDEO}
                    aria-label={
                      en
                        ? 'VraagMijnOverheid — close-up of the search report'
                        : 'VraagMijnOverheid — detailopname van het zoekrapport'
                    }
                  />
                  <div className="case-feature__media-overlay"></div>
                </div>
                <span className="case-feature__bubble">
                  <span className="case-feature__bubble-dot"></span>
                  <span className="case-feature__bubble-live">Pilot</span>
                  <span>· JenV · 2026</span>
                </span>
                <div className="case-feature__logo case-feature__logo--color">
                  <img
                    src="/assets/website/logos/jenv-wit.svg"
                    alt={
                      en
                        ? 'Ministry of Justice and Security'
                        : 'Ministerie van Justitie en Veiligheid'
                    }
                  />
                </div>
              </div>
              <div className="case-feature__body">
                <p className="case-feature__label">
                  {en ? 'Pilot demonstration' : 'Demonstratie pilot'}
                </p>

                <div className="case-feature__stats">
                  <div>
                    <p className="case-feature__stat-num">{en ? '66,000+' : '66.000+'}</p>
                    <p className="case-feature__stat-label">
                      {en ? 'Documents indexed' : 'Documenten geïndexeerd'}
                    </p>
                  </div>
                  <div>
                    <p className="case-feature__stat-num">100+</p>
                    <p className="case-feature__stat-label">
                      {en ? 'Users' : 'Gebruikers'}
                    </p>
                  </div>
                  <div>
                    <p className="case-feature__stat-num">20+</p>
                    <p className="case-feature__stat-label">
                      {en ? 'Request reports downloaded' : 'Verzoekrapporten gedownload'}
                    </p>
                  </div>
                </div>

                <p className="case-feature__quote">
                  {en
                    ? '"It turns a citizen\'s question into a form we can both move forward with."'
                    : '"Het zet de vraag van een burger om in een vorm waar we beiden mee vooruit kunnen."'}
                </p>
                <p className="case-feature__author">
                  {en
                    ? '— Policy officer, Woo intake team, Ministry of Justice and Security'
                    : '— Beleidsmedewerker, Woo-intaketeam, Ministerie van Justitie en Veiligheid'}
                </p>

                <ul className="case-feature__usps">
                  <li>
                    {en
                      ? 'Fits your existing Woo process, without complex integrations.'
                      : 'Sluit aan op bestaand Woo-proces, zonder complexe integraties.'}
                  </li>
                  <li>
                    {en
                      ? 'An explainer video helps citizens understand how the front portal works.'
                      : 'Uitleg video helpt burgers met de werking van het voorportaal.'}
                  </li>
                  <li>
                    {en
                      ? 'Works alongside ongoing central-government Woo initiatives, with room to connect.'
                      : 'Werkt naast lopende rijks-initiatieven binnen de Woo, met kansen om te koppelen.'}
                  </li>
                </ul>
                <a
                  className="link case-feature__link"
                  href="https://woo.thoughtful-oasis.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {en ? 'See the tool live' : 'Bekijk de tool live'}
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* §5 Features */}
        <section className="section">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">{en ? 'Features' : 'Features'}</span>
              <h2>{en ? 'Easy to use, and clear.' : 'Gebruiksvriendelijk en duidelijk.'}</h2>
              <p className="lede" style={{ marginTop: 24 }}>
                {en
                  ? 'The interpretable AI system underneath is what makes these features possible.'
                  : 'Het onderliggende interpreteerbare AI-systeem maakt veel gebruiksvriendelijke features mogelijk.'}
              </p>
            </Reveal>
          </div>
          <div className="vmo-patterns">
            <Reveal as="article" className="vmo-pattern">
              <div className="vmo-pattern__asset vmo-pattern__asset--video">
                <video
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  src={FEATURE_SEARCH_TERMS_VIDEO}
                  aria-label={
                    en
                      ? 'The search terms used are shown to the user'
                      : 'De gebruikte zoektermen worden aan de gebruiker getoond'
                  }
                />
              </div>
              <div className="vmo-pattern__body">
                <p className="pattern-card__overline">
                  {en ? 'Search terms' : 'Zoektermen'}
                </p>
                <h3 className="pattern-card__title">
                  {en
                    ? 'The user sees which words were searched.'
                    : 'De gebruiker ziet met welke woorden er gezocht is.'}
                </h3>
                <p className="pattern-card__statement">
                  {en
                    ? 'Every search is laid open: which terms were used and what they returned. That way the user knows exactly what has already been done, can say something about it, and can steer — add a term, drop a term, search again.'
                    : 'Elke zoekactie staat er open bij: welke termen zijn gebruikt en wat die opleverden. Zo weet de gebruiker precies wat er al is gedaan, kan hij er iets over zeggen, en kan hij bijsturen — een term erbij, een term eraf, opnieuw zoeken.'}
                </p>
              </div>
            </Reveal>
            <Reveal as="article" className="vmo-pattern">
              <div className="vmo-pattern__asset vmo-pattern__asset--video">
                <video
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  src={FEATURE_PAGE_SEARCH_VIDEO}
                  aria-label={
                    en
                      ? 'The system points to the page that holds the information'
                      : 'Het systeem wijst de pagina aan waar de informatie staat'
                  }
                />
              </div>
              <div className="vmo-pattern__body">
                <p className="pattern-card__overline">
                  {en ? 'Page-level search' : 'Pagina-specifiek zoeken'}
                </p>
                <h3 className="pattern-card__title">
                  {en
                    ? 'The right page in a report of hundreds.'
                    : 'De juiste pagina in een rapport van honderden.'}
                </h3>
                <p className="pattern-card__statement">
                  {en
                    ? 'A Woo decision quickly runs to hundreds of pages. Instead of handing back the whole document, the system points to the pages that hold what the user is looking for.'
                    : "Een Woo-besluit is al snel honderden pagina's. In plaats van het hele document terug te geven, wijst het systeem de pagina's aan waar staat wat de gebruiker zoekt."}
                </p>
              </div>
            </Reveal>
            <Reveal as="article" className="vmo-pattern">
              <div className="vmo-pattern__asset vmo-pattern__asset--video">
                <video
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  src={FEATURE_DOCUMENT_VIDEO}
                  aria-label={
                    en
                      ? 'The user opens the published source and downloads the document'
                      : 'De gebruiker bekijkt de publicatiebron en downloadt het document'
                  }
                />
              </div>
              <div className="vmo-pattern__body">
                <p className="pattern-card__overline">
                  {en ? 'Document research' : 'Documentonderzoek'}
                </p>
                <h3 className="pattern-card__title">
                  {en
                    ? 'Click through to the publication itself.'
                    : 'Doorklikken naar de publicatie zelf.'}
                </h3>
                <p className="pattern-card__statement">
                  {en
                    ? 'For every answer, the user can open the published source it came from, and download the document.'
                    : 'Bij elk antwoord kan de gebruiker de publicatiebron erbij pakken waar het uit komt, en het document downloaden.'}
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal as="p" style={{ textAlign: 'center', marginTop: 56 }}>
            <Link className="btn btn-primary" to="/contact?prefill=vmo-intake">
              {en ? 'Schedule an intake' : 'Plan een intake'}
            </Link>
          </Reveal>
        </section>

        {/* §6 FAQ */}
        <section className="section">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">FAQ</span>
              <h2>
                {en ? 'Frequently asked questions.' : 'Veelgestelde vragen.'}
              </h2>
            </Reveal>
            <Reveal style={{ maxWidth: 'var(--max-w-prose)', margin: '0 auto' }}>
              {(en
                ? [
                    {
                      q: 'What is the difference with the BZK Woo tool?',
                      a: 'That tool focuses on the back end: management and search tooling for clerks during processing. We sit at the front end: getting citizens to ask more precisely, surfacing public documents directly, and preventing requests where possible. The two are interoperable, not in competition.',
                    },
                    {
                      q: 'What if the system finds nothing, or misses something?',
                      a: 'Then it says so. The system shows clearly whether a sub-question can be answered with information that is already public. If it cannot, the user decides how to proceed. VraagMijnOverheid can help there by explaining the difference between an information request and a Woo request.',
                    },
                    {
                      q: 'Does this replace the desk or the phone?',
                      a: 'No. The front portal sits alongside your existing channels. Anyone who would rather call or email does that, and every existing route for filing keeps working — including if the front portal is switched off.',
                    },
                    {
                      q: 'Can the citizen see where their request is and how long it will take?',
                      a: 'Not at the moment. VraagMijnOverheid can set out what to expect on behalf of the government organisation. Earlier versions could track the handling of requests, but for the pilot we decided to leave that out.',
                    },
                    {
                      q: 'How do we know whether it works?',
                      a: 'By eleven signals fixed before the pilot started: how many conversations there are, how many search reports get downloaded, how many of those come back as a formal request, and how much preparatory work the case handler is left with. We expect to be able to share these results with interested parties at the end of September.',
                    },
                    {
                      q: 'Where is the data stored?',
                      a: 'EU-bound hosting. On-premise available on request for ministries with that requirement. No model training on ministry data: processing stays within the pilot domain.',
                    },
                    {
                      q: 'Does data from non-public documents end up in the system?',
                      a: 'No. The front portal searches only what has already been published. There is no access to case files, case-management systems or personal data. That is a design choice, not a setting someone can flip by accident.',
                    },
                    {
                      q: 'What happens to what citizens type in?',
                      a: 'The conversation is kept so the search report can be produced and so we can see whether the front portal works. This data is deleted after 90 days, in line with our privacy policy. Users only have to share personal data at the point of filing: for most users, we do not know who they are.',
                    },
                    {
                      q: 'Do we need a DPIA for this, and how does it fall under the AI Act?',
                      a: "The DPIA — the data protection impact assessment required for higher-risk processing — is the ministry's to make: whoever decides to deploy the front portal is the data controller. We supply what you need to complete it: which data passes through, how long it is kept, and which sub-processors are involved. Under the AI Act, the front portal falls under the transparency obligation of article 50: on the first screen the user reads that they are talking to an AI service, that it takes no decisions and gives no legal advice.",
                    },
                    {
                      q: 'Can we plug this into our current Woo workflow?',
                      a: 'Yes. The search report arrives by default as a PDF in your email: workable from any existing Woo mailbox or case-management system. Deeper integrations (API connection to your case system) are a separate implementation phase.',
                    },
                    {
                      q: 'How long does a pilot take with us?',
                      a: 'First pilot: three months from intake to going live on your website. Optionally extended with a test phase (test design, dashboards, helpdesk).',
                    },
                    {
                      q: "Are exports called a 'Woo request'?",
                      a: 'No. Only at the point of filing do we call it a request. The preparatory stage, and the file the citizen can download, is called a search report. That report states again that it is not itself a Woo request.',
                    },
                  ]
                : [
                    {
                      q: 'Wat is het verschil met de BZK Woo-tool?',
                      a: 'Die tool richt zich op de achterkant: management en zoek-tooling voor ambtenaren tijdens verwerking. Wij zitten aan de voorkant: de burger precieser laten vragen, publieke documenten direct tonen, en verzoeken voorkomen waar dat kan. De twee zijn koppelbaar, niet in concurrentie.',
                    },
                    {
                      q: 'Wat als het systeem niets vindt, of iets mist?',
                      a: 'Het systeem laat duidelijk zien of een deelvraag te beantwoorden is met reeds publieke informatie. Als die niet beschikbaar is, dan kan de gebruiker bepalen hoe die verder wil gaan. VraagMijnOverheid kan hierbij helpen door het verschil te duiden tussen informatie- en Woo-verzoeken.',
                    },
                    {
                      q: 'Vervangt dit het loket of de telefoon?',
                      a: 'Nee. Het voorportaal staat naast jullie bestaande kanalen. Wie liever belt of mailt doet dat, en alle bestaande indieningswegen blijven werken — ook als het voorportaal eruit gaat.',
                    },
                    {
                      q: 'Kan de burger zien waar zijn verzoek is en hoe lang het duurt?',
                      a: 'Op dit moment niet. VraagMijnOverheid kan delen wat de verwachtingen zijn namens de overheidsorganisatie. In eerdere versies kon VraagMijnOverheid het verwerken van de verzoeken bijhouden, maar voor de pilot is besloten dit achterwege te laten.',
                    },
                    {
                      q: 'Hoe weten we of het werkt?',
                      a: 'Aan elf signalen die vóór de start zijn vastgelegd: hoeveel gesprekken er zijn, hoeveel zoekrapporten worden gedownload, hoeveel daarvan als formeel verzoek terugkomen, en wat de behandelaar aan voorwerk overhoudt. We verwachten deze resultaten eind september te kunnen delen met geïnteresseerden.',
                    },
                    {
                      q: 'Waar staat de data?',
                      a: 'EU-bound hosting. On-premise mogelijk op verzoek voor ministeries met die eis. Geen modeltraining op ministerie-data: verwerking blijft binnen het pilot-domein.',
                    },
                    {
                      q: 'Komen er gegevens uit niet-openbare documenten in het systeem?',
                      a: 'Nee. Het voorportaal zoekt alleen in wat al gepubliceerd is. Er is geen toegang tot dossiers, zaaksystemen of persoonsgegevens. Dat is een ontwerpkeuze en geen instelling die iemand per ongeluk anders zet.',
                    },
                    {
                      q: 'Wat gebeurt er met wat burgers intypen?',
                      a: 'Het gesprek wordt bewaard om er het zoekrapport van te kunnen maken en om te kunnen zien of het voorportaal werkt. Deze gegevens worden na 90 dagen verwijderd, conform ons privacybeleid. Gebruikers hoeven alleen bij indiening persoonsgegevens te delen: van de meeste gebruikers weten we dus niet wie het zijn.',
                    },
                    {
                      q: 'Hebben we hier een DPIA voor nodig, en hoe valt dit onder de AI-verordening?',
                      a: 'De DPIA — de verplichte privacytoets bij gegevensverwerking met risico — is aan het ministerie: wie besluit het voorportaal in te zetten is verwerkingsverantwoordelijke. Wij leveren wat je nodig hebt om die toets te vullen: welke gegevens er langskomen, hoe lang ze blijven staan en welke onderliggende leveranciers erbij betrokken zijn. Onder de AI-verordening valt het voorportaal onder de transparantieplicht van artikel 50: de gebruiker leest bij het eerste scherm dat hij met een AI-dienst praat, dat die geen besluiten neemt en geen juridisch advies geeft.',
                    },
                    {
                      q: 'Kunnen we dit koppelen met onze huidige Woo-workflow?',
                      a: 'Ja. Het zoekrapport komt standaard binnen als PDF via e-mail: werkbaar vanuit elke bestaande Woo-mailbox of zaaksysteem. Voor diepere integraties (API-koppeling met jullie zaaksysteem) is een aparte implementatiefase.',
                    },
                    {
                      q: 'Hoe lang duurt een pilot bij ons?',
                      a: 'Eerste pilot: drie maanden van intake tot live op je website. Eventueel uitgebreid met een testfase (test-design, dashboards, helpdesk).',
                    },
                    {
                      q: "Heten exports een 'Woo-verzoek'?",
                      a: 'Nee. Pas bij indiening spreken we van een verzoek. De voorbereidende fase en het dossier dat de burger kan downloaden heet een zoekrapport. In dit rapport wordt nog eens toegelicht dat dit op zichzelf geen Woo-verzoek is.',
                    },
                  ]
              ).map((item, idx) => (
                <details className="disclosure" key={idx}>
                  <summary className="disclosure__summary">
                    <div>
                      <p className="disclosure__title">{item.q}</p>
                    </div>
                    <span className="disclosure__icon" aria-hidden="true"></span>
                  </summary>
                  <div className="disclosure__body">
                    <p className="disclosure__desc">{item.a}</p>
                  </div>
                </details>
              ))}

              <details className="disclosure">
                <summary className="disclosure__summary">
                  <div>
                    <p className="disclosure__title">
                      {en
                        ? 'Which research underpins the numbers on this page?'
                        : 'Welk onderzoek onderbouwt de cijfers op deze pagina?'}
                    </p>
                  </div>
                  <span className="disclosure__icon" aria-hidden="true"></span>
                </summary>
                <div className="disclosure__body">
                  <p className="disclosure__desc">
                    {en ? (
                      <>
                        The BZK study on Woo execution burden of May 2026, carried out across 250+
                        organisations including all ministries. Submitted to the House of
                        Representatives as an annex to the{' '}
                        <a
                          className="link"
                          href="https://app.1848.nl/document/tkapi/585141"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          open-government progress letter of 13 May 2026
                        </a>
                        . The figures concern reference year 2024. Earlier, smaller inventories
                        existed (such as Platform AI &amp; Overheid, February 2026, which lists
                        VraagMijnOverheid as an example of AI at the front of the Woo process); the
                        May-2026 study is the most representative picture to date.
                      </>
                    ) : (
                      <>
                        Het BZK-onderzoek naar uitvoeringslasten Woo van mei 2026, uitgevoerd onder
                        250+ organisaties waaronder alle ministeries. Aangeboden aan de Tweede
                        Kamer als bijlage bij de{' '}
                        <a
                          className="link"
                          href="https://app.1848.nl/document/tkapi/585141"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          voortgangsbrief open overheid van 13 mei 2026
                        </a>
                        . De cijfers gaan over peiljaar 2024. Eerder verschenen er kleinere
                        inventarisaties (zoals Platform AI &amp; Overheid, februari 2026, waar
                        VraagMijnOverheid in is opgenomen als voorbeeld van AI aan de voorkant van
                        het Woo-proces); het mei-2026 onderzoek is het meest representatieve beeld
                        tot nu toe.
                      </>
                    )}
                  </p>
                </div>
              </details>

            </Reveal>
          </div>
        </section>
      </main>
    </WebsiteLayout>
  );
};

export default VraagMijnOverheidProductPage;
